import { addDays, format, startOfDay } from "date-fns";
import { Theme } from "../../../../../providers/ThemeProvider";
import { BiomarkerItem, BiomarkerRange } from "../../../../../common/entities/type";
import { metricDataType } from "../../../../../providers/dataPlatformReportTypes";
import {
  getCategoryColor,
  formatBiomarkerTabValue,
  getHighLowLabel,
  getShortTimeValue,
  isHighLowMetric,
  SCORE_CATEGORY_LABELS,
} from "../../../store/helper";
import {
  getBiomarkerBadge,
  getBiomarkerPointerColor,
  getChangeInfo,
  getBiomarkerHatchColors,
  getChartEntries,
  getRangeBounds,
  getRangeBandColor,
  hasValidRanges,
} from "../../healthOverview/biomarkerDisplay";
import {
  ChartBand,
  ChartRange,
  getBandedDomain,
} from "../../score/CustomLineChart";
import { TimelineWindow, toDayCenter, toTime } from "./timeScale";

/**
 * One stacked row of the Timeline chart, whatever its source.
 *
 * - **Biomarkers** (`GetBiomarkers`): the reference-range segments as bands,
 *   dots colored by each reading's own category — the same bands and colors
 *   as the biomarker side panel's trend chart.
 * - **PNOĒ metrics** (the report's `dashboard.metrics`): 0–100 % scores on the
 *   Score screen's five 20 % segments. Metabolic flexibility is the
 *   exception: two values only (100 High, 10 Low), plotted at 75 / 25 with
 *   High/Low ticks and no bands, as `CustomLineChart` does.
 * - **Wearables** (`GetGraphData`): daily values, with the goal as an optional
 *   dashed line.
 */
export type TimelineSeriesGroup = "metric" | "biomarker" | "wearable";

export interface TimelinePoint {
  t: number;
  /** Plotted value — for High/Low metrics the 75 / 25 stand-in. Null on a
   *  wearable day that has a goal but no reading. */
  value: number | null;
  /** That day's goal, wearables only — drawn as the dashed target series. */
  target?: number | null;
  /** A range reading ("<9"), drawn as a hatched bar — biomarkers only. */
  range?: ChartRange;
  /** Tooltip text, unit included. */
  display: string;
  /** The reading's category and that category's bounds, biomarkers only —
   *  "Normal · 14–22 %". */
  category?: string;
  color: string;
  /** False for the neighbours just outside the window, kept so the line runs
   *  in from the edge; they get no dot. */
  inWindow: boolean;
}

/** One reference-range segment for the row's legend: "Normal", "1.65–2.7 %". */
export interface TimelineSegment {
  label: string;
  range: string;
  /** The category's dot colour — the bands behind the line are its tint. */
  color: string;
}

export interface TimelineSeries {
  key: string;
  label: string;
  unit: string;
  group: TimelineSeriesGroup;
  points: TimelinePoint[];
  bands?: ChartBand[];
  /** The bands named and bounded, top to bottom — biomarkers only. */
  segments?: TimelineSegment[];
  domain: [number, number];
  /** The two labels beside the row: bottom and top. */
  yTicks: { value: number; label: string }[];
  subtitle?: string;
  delta?: { direction: "up" | "down" | "flat"; text: string };
  /** Average of the period's daily targets, wearables only. */
  goal?: number | null;
  /** Shown in place of the chart when this series can't be drawn for the
   *  period — wearables have no yearly data. */
  unavailable?: string;
}

export interface TimelineMetricOption {
  key: string;
  label: string;
  unit: string;
  group: TimelineSeriesGroup;
}

// Keys are prefixed by source: a report metric, a biomarker and a wearable key
// can otherwise share an id.
export const seriesKey = (group: TimelineSeriesGroup, id: string) =>
  `${group}:${id}`;

export const parseSeriesKey = (
  key: string
): { group: TimelineSeriesGroup; id: string } => {
  const index = key.indexOf(":");
  return {
    group: key.slice(0, index) as TimelineSeriesGroup,
    id: key.slice(index + 1),
  };
};

export const formatSeriesValue = (value: number, unit: string): string => {
  if (unit === "hr") return getShortTimeValue(value);
  if (Math.abs(value) >= 100) return String(Math.round(value));
  return String(Number(value.toFixed(1)));
};

/** "3010 kcal"; hours are already formatted as h:mm, so they get no unit. */
export const withUnit = (text: string, unit: string) =>
  unit && unit !== "hr" ? `${text} ${unit}` : text;

const tickLabel = (value: number) =>
  Math.abs(value) >= 100 ? String(Math.round(value)) : value.toFixed(1);

const sinceLabel = (t: number) => format(new Date(t), "MMM ''yy");

/**
 * The readings in the window plus the nearest one on either side, so the
 * line enters and leaves at the edges rather than starting at the first dot.
 */
export const pickWindowPoints = <T extends { t: number }>(
  sorted: T[],
  window: TimelineWindow
): (T & { inWindow: boolean })[] => {
  const inside = sorted.filter(
    (p) => p.t >= window.startMs && p.t < window.endMs
  );
  const before = sorted.filter((p) => p.t < window.startMs).pop();
  const after = sorted.find((p) => p.t >= window.endMs);
  return [
    ...(before ? [{ ...before, inWindow: false }] : []),
    ...inside.map((p) => ({ ...p, inWindow: true })),
    ...(after ? [{ ...after, inWindow: false }] : []),
  ];
};

// Fits values (and a goal) with a little headroom, from 0 for all-positive
// data so small changes don't read as dramatic swings.
const autoDomain = (values: number[]): [number, number] => {
  if (values.length === 0) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const top = max + (max - min) * 0.15 || max * 1.15 || 1;
  const bottom = min >= 0 ? 0 : min - (max - min) * 0.15;
  return [Number(bottom.toFixed(1)), Number(top.toFixed(1))];
};

const minMaxTicks = ([min, max]: [number, number]) => [
  { value: min, label: tickLabel(min) },
  { value: max, label: tickLabel(max) },
];

// ─── Biomarkers ──────────────────────────────────────────────────────────────

const NORMAL_CATEGORY = 2;

/** "14–22 %", "< 100 mg/dL", "> 40 mg/dL" — the same limits the range bar
 *  prints. */
const rangeText = (range: BiomarkerRange, unit: string): string => {
  if (range.min === null && range.max !== null)
    return withUnit(`< ${range.max}`, unit);
  if (range.max === null && range.min !== null)
    return withUnit(`> ${range.min}`, unit);
  return withUnit(`${range.min}–${range.max}`, unit);
};

/** "Normal range 14–22 %", "Normal range < 100 mg/dL". */
const normalRangeText = (item: BiomarkerItem): string | undefined => {
  if (!hasValidRanges(item)) return undefined;
  const normal = item.ranges!.find((r) => r.category === NORMAL_CATEGORY);
  if (!normal) return undefined;
  return `Normal range ${rangeText(normal, item.unit || "")}`;
};

/**
 * The reading's category for its tooltip — the badge name the side panel
 * uses and the bounds of its range, "Normal · 14–22 %".
 *
 * Only the backend's own `scoreCategory` for that reading counts: nothing is
 * derived from the ranges here, so an unscored reading (drawn grey) gets no
 * category line. "Out of range" can sit on both ends, so the category's
 * range holding the value is the one printed. The name stands alone when
 * there are no valid ranges, or when none of the category's ranges holds
 * the value — a reading scored against the ranges of its own date can sit
 * outside today's (Bicarbonate 27 scored Out of range, Borderline today).
 */
const categoryText = (
  item: BiomarkerItem,
  scoreCategory: number | undefined,
  value: number,
  theme: Theme
): string | undefined => {
  if (scoreCategory === undefined) return undefined;
  const label = getBiomarkerBadge(scoreCategory, theme)?.label;
  if (!label) return undefined;
  if (!hasValidRanges(item)) return label;
  const range = item
    .ranges!.filter((r) => r.category === scoreCategory)
    .find(
      (r) => (r.min === null || value >= r.min) && (r.max === null || value <= r.max)
    );
  return range ? `${label} · ${rangeText(range, item.unit || "")}` : label;
};

export const buildBiomarkerSeries = (
  item: BiomarkerItem,
  window: TimelineWindow,
  theme: Theme
): TimelineSeries => {
  const unit = item.unit || "";
  // Plain readings and range readings ("<9"); a range sits on its bound (the
  // midpoint of a two-sided "a–b") and is drawn as a hatched bar, in its
  // category's hatch or grey — the same as the biomarker side panel.
  const sorted = getChartEntries(item)
    .map((entry) => ({ entry, t: toDayCenter(entry.dateCreated) }))
    .filter((e): e is { entry: typeof e.entry; t: number } => e.t !== null)
    .map(({ entry, t }) => {
      const bounds = getRangeBounds(entry);
      const value = bounds
        ? bounds.lower !== null && bounds.upper !== null
          ? (bounds.lower + bounds.upper) / 2
          : ((bounds.upper ?? bounds.lower) as number)
        : entry.value;
      return { entry, t, value, bounds };
    })
    .sort((a, b) => a.t - b.t);

  const points = pickWindowPoints(sorted, window).map(
    ({ entry, t, value, bounds, inWindow }) => ({
      t,
      value,
      display: bounds
        ? formatBiomarkerTabValue(entry, unit)
        : withUnit(formatSeriesValue(entry.value, unit), unit),
      category: categoryText(item, entry.scoreCategory, value, theme),
      color:
        entry.scoreCategory !== undefined
          ? getBiomarkerPointerColor(entry.scoreCategory, theme)
          : theme.colorTokens.N[70],
      range: bounds
        ? { ...bounds, ...getBiomarkerHatchColors(entry.scoreCategory ?? 0, theme) }
        : undefined,
      inWindow,
    })
  );

  const bands = hasValidRanges(item)
    ? item.ranges!.map((r) => ({
        from: r.min,
        to: r.max,
        color: getRangeBandColor(r.category, theme),
      }))
    : undefined;
  // The legend reads top to bottom like the plot, so the highest range first.
  const segments = hasValidRanges(item)
    ? [...item.ranges!].reverse().map((r) => ({
        label: getBiomarkerBadge(r.category, theme)?.label ?? "",
        range: rangeText(r, unit),
        color: getBiomarkerPointerColor(r.category, theme),
      }))
    : undefined;
  // Fit to every reading, not just the window's, so paging through periods
  // keeps one scale per biomarker.
  // A range's bounds count too, so its bar has room on the scale.
  const allValues = sorted.flatMap((s) => [
    { value: s.value },
    ...[s.bounds?.lower, s.bounds?.upper]
      .filter((v): v is number => typeof v === "number")
      .map((v) => ({ value: v })),
  ]);
  const banded = bands ? getBandedDomain(bands, allValues, unit) : null;
  const domain = banded
    ? banded.domain
    : autoDomain(allValues.map((v) => v.value));

  // The backend's change against the previous reading, exactly as the
  // biomarker cards show it ("↓207%", via `getChangeInfo`): nothing is
  // computed here. "since" is the backend's `previousDate`.
  const change = getChangeInfo(item);
  const delta = change
    ? {
        direction: change.direction,
        text: `${change.pct}%  since ${sinceLabel(
          toTime(change.previousDate) ?? Date.now()
        )}`,
      }
    : undefined;

  return {
    key: seriesKey("biomarker", item.biomarkerID),
    label: item.name || item.shortName || "",
    unit,
    group: "biomarker",
    points,
    bands,
    segments,
    domain,
    yTicks: minMaxTicks(domain),
    subtitle: normalRangeText(item),
    delta,
  };
};

// ─── PNOĒ metrics ────────────────────────────────────────────────────────────

/** The Score screen's 20 % segments (`getCategoryColor("%")`), lightened to
 *  band shades. */
export const getMetricScoreBands = (theme: Theme): ChartBand[] => [
  { from: 0, to: 20, color: theme.colorTokens.D[10] },
  { from: 20, to: 40, color: theme.colorTokens.W[10] },
  { from: 40, to: 60, color: theme.colorTokens.P[10] },
  { from: 60, to: 80, color: theme.colorTokens.S[10] },
  { from: 80, to: 100, color: theme.colorTokens.S[20] },
];

// Metabolic flexibility is stored as 100 (High) or 10 (Low).
const highLowPlotValue = (value: number) => (value === 100 ? 75 : 25);

export const buildMetricSeries = (
  metric: metricDataType,
  window: TimelineWindow,
  theme: Theme
): TimelineSeries => {
  const highLow = isHighLowMetric(metric.id);
  const sorted = (metric.values ?? [])
    .map((v) => ({ value: v.value, t: toDayCenter(v.dateCreated) }))
    .filter((v): v is { value: number; t: number } => v.t !== null)
    .sort((a, b) => a.t - b.t);

  const points = pickWindowPoints(sorted, window).map(({ value, t, inWindow }) => ({
    t,
    value: highLow ? highLowPlotValue(value) : value,
    display: highLow ? getHighLowLabel(value) : `${Math.round(value)} %`,
    color: getCategoryColor("%", value, theme),
    inWindow,
  }));

  return {
    key: seriesKey("metric", metric.id),
    label: metric.label,
    unit: highLow ? "" : "%",
    group: "metric",
    points,
    bands: highLow ? undefined : getMetricScoreBands(theme),
    domain: [0, 100],
    yTicks: highLow
      ? [
          { value: 25, label: "Low" },
          { value: 75, label: "High" },
        ]
      : [
          { value: 0, label: "0" },
          { value: 100, label: "100" },
        ],
    subtitle: highLow
      ? `Current: ${getHighLowLabel(metric.value)}`
      : `Score ${Math.round(metric.value)} % · ${
          SCORE_CATEGORY_LABELS[metric.scoreCategory] ?? ""
        }`,
  };
};

// ─── Wearables ───────────────────────────────────────────────────────────────

export interface WearableDay {
  date: string;
  current: number | null;
  target: number | null;
}

// `GetGraphData` answers in two shapes: `daily` for the average-only keys and
// `daily_goals` with current/target for the rest. Zeros are "no data" unless
// the key says a zero is real.
const DAILY_ONLY_KEYS = [
  "sleep-quality",
  "resting-heart-rate",
  "heart-rate-variability",
  "sleep-heart-rate-variability",
];

export const normalizeWearableResponse = (
  key: string,
  response: any,
  showZeroValues?: boolean
): WearableDay[] => {
  const nullIfZero = (v: any) =>
    typeof v !== "number" || (v === 0 && !showZeroValues) ? null : v;
  if (DAILY_ONLY_KEYS.includes(key)) {
    return (response?.daily ?? []).map((item: any) => ({
      date: item.date,
      current: nullIfZero(key === "sleep-quality" ? item.value : item.avg_value),
      target: null,
    }));
  }
  return (response?.daily_goals ?? []).map((item: any) => ({
    date: item.date,
    current: nullIfZero(item.goal?.current),
    target: nullIfZero(item.goal?.target),
  }));
};

export const hasWearableGoal = (key: string) => !DAILY_ONLY_KEYS.includes(key);

/**
 * Does a zero reading count as a value on the Timeline? The graphs endpoint
 * answers every day of the period, zero where nothing was logged. For
 * nutrition that zero is real — nothing eaten — so the day gets a dot at 0,
 * the same as the micronutrients (whose `showZeroValues` already says so).
 * An activity or recovery zero (0 kcal burned, 0 h slept) is a missing
 * reading and stays a gap.
 */
export const keepsZeroReadings = (
  info: { type?: string; showZeroValues?: boolean } | undefined
): boolean => Boolean(info?.showZeroValues || info?.type === "nut");

export const buildWearableSeries = (
  option: TimelineMetricOption,
  days: WearableDay[],
  window: TimelineWindow,
  theme: Theme,
  now: number = Date.now()
): TimelineSeries => {
  const unit = option.unit;
  // The endpoint answers the whole week or month, future days included, as
  // zeros; nothing can have been logged there yet, so those days are dropped
  // — reading and goal alike, as the dashboard chart does.
  const endOfToday = addDays(startOfDay(now), 1).getTime();
  const byDay = days
    .map((d) => ({ t: toDayCenter(d.date), value: d.current, target: d.target }))
    .filter(
      (d): d is { t: number; value: number | null; target: number | null } =>
        d.t !== null &&
        d.t < endOfToday &&
        (d.value !== null || d.target !== null)
    )
    .sort((a, b) => a.t - b.t);
  const readings = byDay.filter(
    (d): d is { t: number; value: number; target: number | null } =>
      d.value !== null
  );

  // Days with a goal but no reading stay in, so the dashed target runs across
  // the whole window; they get no reading dot.
  const points = byDay
    .filter((d) => d.t >= window.startMs && d.t < window.endMs)
    .map((d) => ({
      t: d.t,
      value: d.value,
      target: d.target,
      display:
        d.value !== null ? withUnit(formatSeriesValue(d.value, unit), unit) : "No reading",
      // Figma reading dots: #F0BD24 = W[70].
      color: theme.colorTokens.W[70],
      inWindow: true,
    }));

  // The goal changes day to day, so the label shows the average of the
  // period's daily targets (user decision); the dashed series shows each day.
  const targets = points
    .map((p) => p.target)
    .filter((t): t is number => typeof t === "number");
  const goal = targets.length
    ? targets.reduce((sum, t) => sum + t, 0) / targets.length
    : null;
  const last = readings[readings.length - 1];
  const previous = readings[readings.length - 2];
  const diff = last && previous ? last.value - previous.value : 0;
  const delta =
    diff !== 0
      ? {
          direction: diff > 0 ? ("up" as const) : ("down" as const),
          text: `${withUnit(formatSeriesValue(Math.abs(diff), unit), unit)} since ${format(
            new Date(previous.t),
            "MMM d"
          )}`,
        }
      : undefined;

  const domain = autoDomain(
    points.flatMap((p) => [p.value, p.target]).filter(
      (v): v is number => typeof v === "number"
    )
  );

  return {
    key: option.key,
    label: option.label,
    unit,
    group: "wearable",
    points,
    domain,
    yTicks:
      unit === "hr"
        ? domain.map((v) => ({ value: v, label: getShortTimeValue(v) }))
        : minMaxTicks(domain),
    subtitle:
      goal !== null
        ? `Avg goal ${withUnit(formatSeriesValue(goal, unit), unit)}`
        : undefined,
    delta,
    goal,
  };
};

/** The graphs endpoint serves weeks and months only, so a year has no
 *  wearable data; the row stays, with a note in place of the chart. */
export const WEARABLE_YEAR_UNAVAILABLE =
  "Wearable data isn't available in the year view";

export const buildUnavailableWearableSeries = (
  option: TimelineMetricOption
): TimelineSeries => ({
  key: option.key,
  label: option.label,
  unit: option.unit,
  group: "wearable",
  points: [],
  domain: [0, 1],
  yTicks: [],
  unavailable: WEARABLE_YEAR_UNAVAILABLE,
});
